import {
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import type { ProjectStatus } from '.'
import ProjectFooter from './ProjectFooter'
import ProjectRow from './ProjectRow'
import StyledSortLabel from './StyleSortLabel'

interface Project {
  name: string
  status: string
  dentist: string
  sharedWith: string[]
  createdBy?: string
  access?: string
  createdAt: string
  lastModifiedAt: string
  id: string
}

interface ProjectsTableProps {
  projects: Project[]
  handleStatusChange: (index: number, newStatus: ProjectStatus) => void
  handleMenuOpen: (event: React.MouseEvent<HTMLElement>, index: number) => void
  tabValue: number
  selectedRows: string[]
  handleCheckboxToggle: (id: string, name: string) => void
  page?: number
  count?: number
  rowsPerPage?: number
  handlePrev?: () => void
  handleNext?: () => void
  statusFilters?: ProjectStatus[]
  setStatusFilters?: (filters: ProjectStatus[]) => void
  ordering?: string
  setOrdering?: (field: string) => void
  refetch: () => void
}

const ProjectsTable = ({
  projects,
  handleStatusChange,
  handleMenuOpen,
  tabValue,
  selectedRows,
  handleCheckboxToggle,
  page,
  count,
  rowsPerPage,
  handlePrev,
  handleNext,
  statusFilters = ['active', 'scheduled', 'archive'],
  setStatusFilters,
  ordering,
  setOrdering,
  refetch,
}: ProjectsTableProps) => {
  const { t } = useTranslation()

  const isSharedProjects = tabValue === 1
  const columnHeaders = isSharedProjects
    ? [
        t('projectsTable.columns.name'),
        t('projectsTable.columns.status'),
        t('projectsTable.columns.dentist'),
        t('projectsTable.columns.access'),
        t('projectsTable.columns.createdAt'),
        t('projectsTable.columns.createdBy'),
        t('projectsTable.columns.lastModifiedAt'),
      ]
    : [
        t('projectsTable.columns.name'),
        t('projectsTable.columns.status'),
        t('projectsTable.columns.dentist'),
        t('projectsTable.columns.sharedWith'),
        t('projectsTable.columns.createdAt'),
        t('projectsTable.columns.lastModifiedAt'),
      ]

  const allSelected =
    projects.length > 0 && selectedRows.length === projects.length

  const handleSelectAllClick = () => {
    if (allSelected) {
      projects.forEach((project) => {
        if (selectedRows.includes(project.id)) {
          handleCheckboxToggle(project.id, project.name)
        }
      })
    } else {
      projects.forEach((project) => {
        if (!selectedRows.includes(project.id)) {
          handleCheckboxToggle(project.id, project.name)
        }
      })
    }
  }

  return (
    <Paper elevation={2} sx={{ backgroundColor: '#1e1e1e' }}>
      <Table>
        <TableHead>
          <TableRow>
            {!isSharedProjects && (
              <TableCell padding="checkbox">
                <Checkbox
                  sx={checkboxStyle}
                  disableRipple
                  checked={allSelected}
                  onChange={handleSelectAllClick}
                />
              </TableCell>
            )}

            {columnHeaders.map((label) => (
              <TableCell
                key={label}
                sx={{
                  ...tableCellStyle,
                  ...(label === t('projectsTable.columns.name') && {
                    width: 250,
                  }),
                }}
              >
                <StyledSortLabel
                  label={label}
                  filters={statusFilters}
                  setFilters={setStatusFilters}
                  ordering={ordering}
                  setOrdering={setOrdering}
                />
              </TableCell>
            ))}

            {<TableCell sx={tableCellStyle} />}
          </TableRow>
        </TableHead>

        <TableBody>
          {projects.map((row, index) => (
            <ProjectRow
              key={row.id}
              index={index}
              row={row}
              handleStatusChange={handleStatusChange}
              handleMenuOpen={handleMenuOpen}
              tabValue={tabValue}
              isSelected={selectedRows?.includes(row.id)}
              onCheckboxToggle={() => handleCheckboxToggle(row?.id, row?.name)}
              refetch={refetch}
            />
          ))}
        </TableBody>
        <ProjectFooter
          page={page}
          count={count}
          rowsPerPage={rowsPerPage}
          handlePrev={handlePrev}
          handleNext={handleNext}
        />
      </Table>
    </Paper>
  )
}

const checkboxStyle = {
  color: 'white',
  '&.Mui-checked': { color: '#90caf9' },
  '& .MuiSvgIcon-root': { fontSize: 20 },
}

const tableCellStyle = {
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '24px',
  letterSpacing: '0.17px',
  color: 'white',
  whiteSpace: 'nowrap',
}

export default ProjectsTable
